import { getContentAvailability } from "@-/dev.improve.haniverse.lib.database.shards";
import type { ActorDatabaseHandle } from "@-/dev.improve.haniverse.database.actor";
import {
  cardReviewSettingsFromUser,
  isCardContentAvailable,
  isCardContentKindReviewEligible,
  resolveUserCardContentKind,
  resolveUserCardSubject,
} from "@-/dev.improve.haniverse.lib.cards.card-content";
import {
  getActorOwnerKey,
  getDeviceSetting,
  setDeviceSetting,
} from "@-/dev.improve.haniverse.lib.device-settings";

type DailyReviewMilestone = ReturnType<typeof getDeviceSetting<"dailyReviewMilestones">>[string];
export type CapturedDailyReviewMilestone = { actorId: string; milestone: DailyReviewMilestone };

/** Capture BEFORE the first rating changes a schedule. Empty cohorts stay empty all day. */
export async function captureDailyReviewMilestone(
  actorDb: ActorDatabaseHandle,
  now: Date,
): Promise<CapturedDailyReviewMilestone | null> {
  const user = await actorDb.query.user.findFirst();
  if (user === undefined) {
    return null;
  }
  const day = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
  // Kept per account on this device. Not by the `user` row's id, which every database shares.
  const actorId = getActorOwnerKey();
  const stored = getDeviceSetting("dailyReviewMilestones")[actorId];
  if (stored?.day === day) {
    return { actorId, milestone: stored };
  }
  const midnight = new Date(now);
  midnight.setHours(0, 0, 0, 0);
  const startedAt = midnight.toISOString();
  const rows = await actorDb.query.userCard.findMany({
    columns: { id: true },
    where: {
      fsrsCard: { due: { lte: startedAt } },
      manuallyMasteredAt: { isNull: true },
      skippedAt: { isNull: true },
      startedStudyingAt: { lt: startedAt },
    },
    with: {
      userGraphemeMeaningCard: { columns: { grapheme: true } },
      userGraphemePronunciationCard: { columns: { grapheme: true } },
      userWordSentenceCard: { columns: { kind: true, word: true } },
    },
  });
  const settings = cardReviewSettingsFromUser(user);
  const contentAvailability = getContentAvailability();
  const cardIds = rows
    .filter((row) => {
      const cardContentKind = resolveUserCardContentKind(row);
      const subject = resolveUserCardSubject(row);
      // The same availability rule as the due counts and the scheduler: a card this catalog cannot
      // serve must not hold the day's milestone open.
      return (
        cardContentKind !== null &&
        subject !== null &&
        isCardContentKindReviewEligible({ cardContentKind, settings }) &&
        isCardContentAvailable({ cardContentKind, contentAvailability, subjectText: subject.text })
      );
    })
    .map((row) => row.id);
  const milestone = { cardIds, completed: false, day, startedAt };
  setDeviceSetting("dailyReviewMilestones", {
    ...getDeviceSetting("dailyReviewMilestones"),
    [actorId]: milestone,
  });
  return { actorId, milestone };
}

/** Only persisted, non-undone successful reviews discharge the frozen carry-over cohort. */
export async function completeDailyReviewMilestone(
  actorDb: ActorDatabaseHandle,
  captured: CapturedDailyReviewMilestone | null,
): Promise<boolean> {
  if (captured === null) {
    return false;
  }
  const { actorId, milestone } = captured;
  if (milestone.completed || milestone.cardIds.length === 0) {
    return false;
  }
  const reviews = await actorDb.query.userCardReview.findMany({
    columns: { cardId: true },
    where: {
      cardId: { in: milestone.cardIds },
      fsrsCardReviewLog: { rating: { gt: 1 } },
      reviewedAt: { gte: milestone.startedAt },
    },
  });
  const reviewedIds = new Set(reviews.map((review) => review.cardId));
  if (!milestone.cardIds.every((id) => reviewedIds.has(id))) {
    return false;
  }
  // Re-read after the async query: overlapping completions cannot emit twice.
  const milestones = getDeviceSetting("dailyReviewMilestones");
  const current = milestones[actorId];
  if (current?.day !== milestone.day || current.completed) {
    return false;
  }
  setDeviceSetting("dailyReviewMilestones", {
    ...milestones,
    [actorId]: { ...current, completed: true },
  });
  return true;
}
